import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  apiDataFetchingErrorsAtom,
  controlsSF,
  errorsCrudContentAtomFamily,
  filterDirtyOrDefaultMetadata,
  filterOnlyDirty,
  getSiblingToValidationFields,
  Log,
  SimpleResult,
} from "@ryde/shared";

import { useCrudEditContext } from "../../contexts";
import { getPayloadForDuplicateSave } from "./duplicate-entity.helpers";

export const useCrudSave = <T>() => {
  const {
    entity,
    data: { isDuplicate, save },
  } = useCrudEditContext<T>();
  const { t } = useTranslation();
  const controls = useRecoilValue(controlsSF(entity));
  const {
    getValues,
    trigger,
    formState: { dirtyFields },
  } = useFormContext();

  const setCrudServerErrors = useSetRecoilState(
    errorsCrudContentAtomFamily(entity),
  );
  const setApiServerErrors = useSetRecoilState(apiDataFetchingErrorsAtom);

  const saveCrudEntity = useEvent(async (dto?: T): Promise<SimpleResult<T>> => {
    setCrudServerErrors(undefined);
    setApiServerErrors(undefined);
    const isValidForm = await trigger();

    if (!isValidForm) {
      Log.warnAndToast(t("errors.formIsInvalidCheckValidationErrors"));
      return new SimpleResult({ success: false });
    }

    if (save === undefined) {
      Log.warnAndToast(
        t("errors.invalidMethodSkipping", { methodName: t("general.SAVE") }),
      );
      return new SimpleResult({ success: false });
    }

    const formData = getValues() as Partial<T>;
    let formDataCleaned: Partial<T>;

    if (isDuplicate) {
      const filteredControlsData = filterDirtyOrDefaultMetadata(
        formData,
        dto,
        dirtyFields,
      );

      formDataCleaned = getPayloadForDuplicateSave(
        entity,
        { ...filteredControlsData, id: null } as Record<string, unknown>,
        true,
      ) as Partial<T>;
    } else {
      const siblingToValidationFields = getSiblingToValidationFields({
        controls,
        formFields: Object.keys(formData),
      });

      formDataCleaned = filterOnlyDirty({
        formFields: formData,
        dirtyFields,
        siblingToValidationFields,
      });
    }

    const saveResult = await save(formDataCleaned);
    return saveResult;
  });

  return { saveCrudEntity };
};
